import { useState, useEffect } from 'react'
import { ChevronDown, ChevronRight, Radio, Users } from 'lucide-react'
import { supabase } from '../lib/supabaseClient'

const S = {
  card: {
    background: 'rgba(28, 38, 38, .6)',
    border: '1px solid rgba(140, 180, 130, .2)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    margin: '0 12px 12px 12px',
  },
  bar: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    background: 'none',
    border: 'none',
    color: '#e0e8de',
    cursor: 'pointer',
  },
  display: {
    fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
  muted: { color: '#8aa090' },
  faded: { color: '#5a6a60' },
  rowLabel: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: 9,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: '#5a6a60',
    minWidth: 92,
  },
  rowValue: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
    color: '#e0e8de',
  },
}

// IdentityCard
// =================================================================
// Renders the player's chain-of-command card on PlayerView. Collapsed:
// faction dot + faction + callsign + team. Expanded: full chain (CO →
// platoon → patrol), radio freq, members list.
//
// Render rule: only when the player has an active event selected AND is
// a member of an MCS node in that event. No node membership = no card.
//
// Query path:
//   1. Find the player's node membership in this event:
//        SELECT node_id, mcs_nodes!inner(event_id)
//          FROM mcs_node_members
//         WHERE user_id = current
//   2. Pull every node for the event in one fetch and walk parent_id in
//      JS — recursive CTEs aren't accessible through PostgREST.
//   3. Patrol = player's node, Platoon = patrol.parent, Faction CO = the
//      topmost ancestor (parent_id IS NULL).
//   4. Resolve assigned_player_id on each ancestor → user, assigned_team_id
//      on the patrol → team, faction_id on the patrol → event_factions.
//   5. Members of the patrol come from mcs_node_members joined to users.
// =================================================================

export default function IdentityCard({ user, activeEventId }) {
  const [identity, setIdentity] = useState(null)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    fetchIdentity()
  }, [user?.id, activeEventId])

  // Realtime: refetch when the player's node membership changes, or when
  // any mcs_node in this event changes (e.g. a GM updates assigned_team_id
  // on the patrol → the chip's team label should flip without a remount).
  // Without these listeners the chip stays stale until next mount / tab
  // switch even though the Teams tab list itself was already covered by
  // PlayerView's team_members listener (e50ae49).
  useEffect(() => {
    if (!user?.id || !activeEventId) return
    const channel = supabase.channel(`identity-${user.id}-${activeEventId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'mcs_node_members', filter: `user_id=eq.${user.id}` },
        () => fetchIdentity()
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'mcs_nodes', filter: `event_id=eq.${activeEventId}` },
        () => fetchIdentity()
      )
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [user?.id, activeEventId])

  const fetchIdentity = async () => {
    if (!user?.id || !activeEventId) { setIdentity(null); return }

    // 1. Player's node membership scoped to this event. The inner-join on
    //    mcs_nodes lets us filter by event_id without a second round-trip.
    const { data: memberships } = await supabase
      .from('mcs_node_members')
      .select('node_id, mcs_nodes!inner(event_id)')
      .eq('user_id', user.id)
    const myNodeId = (memberships || [])
      .find(r => r.mcs_nodes?.event_id === activeEventId)?.node_id
    if (!myNodeId) { setIdentity(null); return }

    // 2. All nodes for the event — needed to walk up the parent chain.
    const { data: allNodes } = await supabase
      .from('mcs_nodes')
      .select('id, name, callsign, parent_id, faction_id, assigned_player_id, assigned_team_id, radio_freq')
      .eq('event_id', activeEventId)
    const byId = Object.fromEntries((allNodes || []).map(n => [n.id, n]))

    const patrol = byId[myNodeId]
    if (!patrol) { setIdentity(null); return }
    const platoon = patrol.parent_id ? byId[patrol.parent_id] : null
    let topAncestor = patrol
    while (topAncestor.parent_id && byId[topAncestor.parent_id]) {
      topAncestor = byId[topAncestor.parent_id]
    }

    // 3. Lookups (faction, team, leaders, patrol members).
    const factionId = patrol.faction_id || platoon?.faction_id || topAncestor?.faction_id || null
    const leaderIds = [
      topAncestor?.assigned_player_id,
      platoon?.assigned_player_id,
      patrol.assigned_player_id,
    ].filter(Boolean)

    const [factionRes, teamRes, usersRes, membersRes] = await Promise.all([
      factionId
        ? supabase.from('event_factions').select('id, name, color').eq('id', factionId).limit(1)
        : Promise.resolve({ data: [] }),
      patrol.assigned_team_id
        ? supabase.from('teams').select('id, name').eq('id', patrol.assigned_team_id).limit(1)
        : Promise.resolve({ data: [] }),
      leaderIds.length > 0
        ? supabase.from('users').select('id, callsign, display_name').in('id', leaderIds)
        : Promise.resolve({ data: [] }),
      supabase
        .from('mcs_node_members')
        .select('user_id, user:users!mcs_node_members_user_id_fkey(id, callsign, display_name)')
        .eq('node_id', patrol.id),
    ])

    const userById = Object.fromEntries((usersRes.data || []).map(u => [u.id, u]))
    const fac = factionRes.data?.[0] || null
    const team = teamRes.data?.[0] || null

    setIdentity({
      factionName: fac?.name || 'UNALIGNED',
      factionColor: fac?.color || '#9adb6e',
      // Bar callsign: prefer node.callsign, fall back to node.name.
      callsign: patrol.callsign || patrol.name,
      teamName: team?.name || null,
      factionCO: topAncestor?.assigned_player_id ? userById[topAncestor.assigned_player_id] : null,
      // Skip the platoon row when the player's node IS the platoon (i.e. the
      // patrol has no further ancestor below the top) so we don't repeat.
      platoonLead: platoon && platoon.id !== topAncestor?.id && platoon.id !== patrol.id
        ? (platoon.assigned_player_id ? userById[platoon.assigned_player_id] : null)
        : null,
      patrolLead: patrol.assigned_player_id ? userById[patrol.assigned_player_id] : null,
      radioFreq: patrol.radio_freq || null,
      // Expanded callsign row: same fallback as the bar (callsign → name)
      // so the row stays meaningful even when callsign isn't set.
      nodeCallsign: patrol.callsign || patrol.name || null,
      members: (membersRes.data || []).map(m => m.user).filter(Boolean),
    })
  }

  if (!identity) return null

  const personDisplay = (p) => p ? `${p.callsign || p.display_name || ''}${p.callsign && p.display_name ? ` (${p.display_name})` : ''}` : null

  // Polish 1: every row is conditional. Empty values hide the row entirely
  // rather than showing an em-dash placeholder. Members row hides when empty
  // for the same reason.
  const factionCOValue = personDisplay(identity.factionCO)
  const platoonLeadValue = personDisplay(identity.platoonLead)
  const patrolLeadValue = personDisplay(identity.patrolLead)
  const memberNames = (identity.members || [])
    .map(m => m.callsign || m.display_name)
    .filter(Boolean)
    .join(', ')

  return (
    <div style={S.card}>
      <button onClick={() => setExpanded(!expanded)} style={S.bar}>
        <span style={{ fontSize: 14 }}>{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: identity.factionColor, display: 'inline-block', flexShrink: 0 }} />
        <span style={{ ...S.display, fontSize: 12, color: identity.factionColor, flexShrink: 0 }}>{identity.factionName}</span>
        <span style={{ ...S.faded }}>—</span>
        <span style={{ ...S.display, fontSize: 12, color: '#9adb6e' }}>{identity.callsign}</span>
        {identity.teamName && (
          <span style={{ ...S.display, fontSize: 11, color: '#4dd6c2' }}>({identity.teamName})</span>
        )}
      </button>

      {expanded && (
        <div style={{ padding: '0 14px 12px 14px', borderTop: '1px solid rgba(140,180,130,.12)', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {factionCOValue && <Row label="Faction CO" value={factionCOValue} />}
          {platoonLeadValue && <Row label="Platoon Lead" value={platoonLeadValue} />}
          {patrolLeadValue && <Row label="Patrol Lead" value={patrolLeadValue} />}
          {identity.radioFreq && <Row label="Radio" value={identity.radioFreq} icon={<Radio size={12} />} />}
          {identity.nodeCallsign && <Row label="Event Unit" value={identity.nodeCallsign} />}
          {memberNames && <Row label="Members" value={memberNames} icon={<Users size={12} />} />}
        </div>
      )}
    </div>
  )
}

function Row({ label, value, icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8 }}>
      <span style={S.rowLabel}>{label}</span>
      <span style={{ ...S.rowValue, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {icon}
        {value}
      </span>
    </div>
  )
}
